/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { DateRange } from '@material-ui/pickers';
import { Grid, IconButton, MenuItem } from '@material-ui/core';
import { SearchOutlined } from '@material-ui/icons';
import { useStyles } from './styles';
import { useCommonStyles } from '../../../../../styles';
import { useApiError } from '../../../../../providers';
import { PartnerPartnership } from '../../../../../models';
import { CheckIcon, DateRangeSelect, HamburgerIcon, MultiSelectControl, SpinnerWithProps } from '../../../../../components';
import { FiltersDialog } from './FiltersDialog';
import { getPartnerPartnerships } from '../../IBClientsCashbackService';

interface Props {
  translation: any;
  dateRange: DateRange<Date>;
  handleDateRangeChange: (date: DateRange<Date>) => void;
  handleButtonClick?: () => void;
  handlePartnershipsChange: (newOptions: string[]) => void;
  handleFilterApplied: (newOptions: string[]) => void;
}

export const ReportToolbarWithFilters: FC<Props> = ({ translation, dateRange, handleDateRangeChange, handleButtonClick, handlePartnershipsChange, handleFilterApplied }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { addError } = useApiError();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>(dateRange);
  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  const getPartnerships = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPartnerPartnerships();
      setPartnerships(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getPartnerships();
    })();
  }, []);

  const requestReport = useCallback(() => {
    if (handleButtonClick) {
      handleButtonClick();
    }
  }, [handleButtonClick]);

  const changeSelectedPartnerships = useCallback(
    async (event) => {
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = ['All'];
      }

      if (options.length > 1) {
        if (options.includes('All')) {
          if (options[options.length - 1] === 'All') {
            options = options.filter((option) => option === 'All');
          } else {
            options = options.filter((option) => option !== 'All');
          }
        }
      }

      setSelectedPartnerships(options);
      handlePartnershipsChange(options);
    },
    [handlePartnershipsChange],
  );

  const renderValueCustomCondition = useCallback(
    (value: string[]) => {
      if (value[0] === 'All') {
        return [translation.all];
      }

      return false;
    },
    [translation.all],
  );

  return (
    <>
      <Grid container direction="row" justify="space-between" alignContent="center" alignItems="center" className={classes.desktopContainer} spacing={1}>
        <SpinnerWithProps isLoading={loading} />
        <Grid item className={classes.filters}>
          <p className={commonClasses.formControlTitle}>{translation.text_6447}</p>
          <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedPartnerships} onChange={(event: any) => changeSelectedPartnerships(event)}>
            <MenuItem className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
              <CheckIcon />
              <span>{translation.all}</span>
            </MenuItem>
            {partnerships.map((partnership) => (
              <MenuItem
                className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                key={partnership.partnershipCode}
                value={partnership.partnershipCode ? partnership.partnershipCode : ''}
              >
                <CheckIcon />
                <span>{partnership.partnershipCode}</span>
              </MenuItem>
            ))}
          </MultiSelectControl>
        </Grid>
        <Grid item className={classes.filters}>
          <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);
              handleDateRangeChange(value);
            }}
          />
        </Grid>
        <Grid item style={{ alignSelf: 'flex-end' }}>
          <IconButton onClick={requestReport}>
            <SearchOutlined style={{ color: '#030C21' }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container direction="row" justify="space-between" className={classes.mobileContainer} alignItems="center">
        <Grid item xs={11}>
          <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);
              handleDateRangeChange(value);
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => setOpenFilterDialog(true)}>
            <HamburgerIcon />
          </IconButton>
        </Grid>
      </Grid>

      <FiltersDialog
        open={openFilterDialog}
        translation={translation}
        partnerships={partnerships}
        onClose={() => setOpenFilterDialog(false)}
        applyFilter={(partnerships) => {
          setOpenFilterDialog(false);
          setSelectedPartnerships(partnerships);
          handleFilterApplied(partnerships);
        }}
        renderValueCustomCondition={renderValueCustomCondition}
      />
    </>
  );
};
